<template>
  <div
    class="language-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="language-modal-title"
  >

    <!-- ─── Header ────────────────────────────────────────────── -->
    <header class="language-modal__header">
      <div class="language-modal__heading">
        <h2 id="language-modal-title" class="language-modal__title">Language</h2>
        <p class="language-modal__subtitle">Choose your preferred language.</p>
      </div>
      <button
        class="language-modal__close"
        @click="emit('close')"
        aria-label="Close language picker"
      >
        <ion-icon :icon="closeOutline" />
      </button>
    </header>

    <!-- ─── Language list ─────────────────────────────────────── -->
    <ul class="language-modal__list" role="listbox" aria-label="Available languages">
      <li v-for="lang in languages" :key="lang.code">
        <button
          class="language-modal__option"
          :class="{
            'language-modal__option--active': lang.code === current,
            'language-modal__option--disabled': !lang.available,
          }"
          role="option"
          :aria-selected="lang.code === current"
          :disabled="!lang.available"
          @click="onSelect(lang)"
        >
          <span class="language-modal__badge" aria-hidden="true">{{ lang.badge }}</span>
          <span class="language-modal__text">
            <span class="language-modal__name">{{ lang.name }}</span>
            <span class="language-modal__native">{{ lang.native }}</span>
          </span>
          <ion-icon
            v-if="lang.code === current"
            :icon="checkmarkCircle"
            class="language-modal__check"
            aria-hidden="true"
          />
          <span v-else-if="!lang.available" class="language-modal__soon">Soon</span>
        </button>
      </li>
    </ul>

    <!-- ─── Footer note + action ──────────────────────────────── -->
    <p class="language-modal__note">More languages are coming soon.</p>
    <button class="language-modal__done" @click="emit('close')">Done</button>

  </div>
</template>

<script setup lang="ts">
import { IonIcon } from '@ionic/vue';
import { closeOutline, checkmarkCircle } from 'ionicons/icons';
import type { LanguageCode } from '@/stores/settings.store';

interface LanguageOption {
  code: LanguageCode;
  name: string;
  native: string;
  badge: string;
  available: boolean;
}

defineProps<{
  /** Currently selected language code. */
  current: LanguageCode;
}>();

const emit = defineEmits<{
  (e: 'select', code: LanguageCode): void;
  (e: 'close'): void;
}>();

// Only English is selectable for now; the rest are listed as upcoming locales.
const languages: LanguageOption[] = [
  { code: 'en',  name: 'English',    native: 'English',            badge: 'EN',  available: true },
  { code: 'fil', name: 'Filipino',   native: 'Filipino / Tagalog', badge: 'FIL', available: false },
  { code: 'ceb', name: 'Cebuano',    native: 'Bisaya',             badge: 'CEB', available: false },
  { code: 'ilo', name: 'Ilocano',    native: 'Ilokano',            badge: 'ILO', available: false },
  { code: 'hil', name: 'Hiligaynon', native: 'Ilonggo',            badge: 'HIL', available: false },
];

function onSelect(lang: LanguageOption): void {
  if (!lang.available) return;
  emit('select', lang.code);
}
</script>

<style scoped lang="scss">
.language-modal {
  background: var(--syntry-surface);
  border-radius: var(--syntry-radius-modal);
  padding: var(--sp-6) var(--sp-5) var(--sp-5);
  display: flex;
  flex-direction: column;

  // ── Header ──
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-5);
  }

  &__heading {
    min-width: 0;
  }

  &__title {
    margin: 0 0 2px;
    font-size: 22px;
    font-weight: 800;
    color: var(--syntry-text-strong);
    letter-spacing: -0.02em;
  }

  &__subtitle {
    margin: 0;
    font-size: var(--syntry-fs-small);
    color: var(--syntry-text-muted);
    line-height: 1.4;
  }

  &__close {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--syntry-bg-soft);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--syntry-text-muted);
    font-size: 20px;
    -webkit-tap-highlight-color: transparent;
  }

  // ── List ──
  &__list {
    list-style: none;
    margin: 0 0 var(--sp-4);
    padding: 0;
    background: var(--syntry-bg);
    border-radius: var(--syntry-radius-md);
    overflow: hidden;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    padding: var(--sp-3) var(--sp-4);
    background: none;
    border: none;
    border-bottom: 1px solid var(--syntry-border);
    text-align: left;
    cursor: pointer;
    min-height: 60px;
    -webkit-tap-highlight-color: transparent;
    transition: background 150ms ease;

    li:last-child & {
      border-bottom: none;
    }

    &:active {
      background: var(--syntry-bg-soft);
    }

    &--active {
      background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));

      &:active {
        background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
      }
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.55;

      &:active {
        background: none;
      }
    }
  }

  &__badge {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--syntry-brand-soft-surface, var(--syntry-teal-soft));
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.02em;

    .language-modal__option--disabled & {
      background: var(--syntry-bg-soft);
      color: var(--syntry-text-muted);
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__name {
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);
    line-height: 1.3;
  }

  &__native {
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-muted);
    line-height: 1.3;
  }

  &__check {
    flex-shrink: 0;
    font-size: 24px;
    color: var(--syntry-primary);
  }

  &__soon {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    color: var(--syntry-text-muted);
    background: var(--syntry-bg-soft);
    border-radius: var(--syntry-radius-chip);
    padding: 3px 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  // ── Footer ──
  &__note {
    margin: 0 0 var(--sp-4);
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-soft);
    text-align: center;
  }

  &__done {
    height: 52px;
    border: none;
    border-radius: var(--syntry-radius-button);
    background: var(--syntry-gradient-primary);
    font-size: 15px;
    font-weight: 700;
    color: #ffffff;
    cursor: pointer;
    box-shadow: var(--syntry-shadow-button);
    -webkit-tap-highlight-color: transparent;
  }
}
</style>
